<!-- eslint-disable -->
<template>
  <div class="aa-dashboard">
    <!-- ===== Header ===== -->
    <div class="dash-header">
      <div>
        <h2 class="dash-title">Applications Analytics</h2>
        <p class="dash-sub">JEEM / SABER / SFDA / MOIAT — live from the ERP, replacing the old Caspio dashboard</p>
      </div>
      <button class="btn-refresh" :disabled="loading" @click="applyFilters">
        <feather-icon icon="RefreshCwIcon" size="16" :class="{ spinning: loading }" />
        <span>Refresh</span>
      </button>
    </div>

    <!-- ===== Filter bar ===== -->
    <div class="filter-bar card">
      <div class="filter-row">
        <div class="filter-field">
          <label>Date range (Allocation Date)</label>
          <div class="filter-field__range">
            <input v-model="filters.from" type="date" class="dash-input">
            <span class="dash-period__sep">→</span>
            <input v-model="filters.to" type="date" class="dash-input">
          </div>
        </div>

        <div class="filter-field">
          <label>Scheme Owner <span class="filter-field__hint">({{ filterOptions.schemeOwners.length }}, ctrl/cmd-click for multiple)</span></label>
          <select v-model="filters.schemeOwner" multiple size="4" class="dash-input dash-input--multi">
            <option v-for="opt in filterOptions.schemeOwners" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <div class="filter-field">
          <label>Certificate Scheme <span class="filter-field__hint">({{ filterOptions.certificateSchemes.length }}, ctrl/cmd-click for multiple)</span></label>
          <select v-model="filters.certificateScheme" multiple size="4" class="dash-input dash-input--multi">
            <option v-for="opt in filterOptions.certificateSchemes" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <div class="filter-field">
          <label>Application Type <span class="filter-field__hint">({{ filterOptions.applicationTypes.length }}, ctrl/cmd-click for multiple)</span></label>
          <select v-model="filters.applicationType" multiple size="4" class="dash-input dash-input--multi">
            <option v-for="opt in filterOptions.applicationTypes" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <div class="filter-field">
          <label>Product Category / Technical Regulation <span class="filter-field__hint">({{ filterOptions.productCategories.length }}, ctrl/cmd-click for multiple)</span></label>
          <select v-model="filters.productCategory" multiple size="4" class="dash-input dash-input--multi">
            <option v-for="opt in filterOptions.productCategories" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <div class="filter-field">
          <label>Processing Office <span class="filter-field__hint">({{ filterOptions.processingOffices.length }}, ctrl/cmd-click for multiple)</span></label>
          <select v-model="filters.processingOffice" multiple size="4" class="dash-input dash-input--multi">
            <option v-for="opt in filterOptions.processingOffices" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <div class="filter-field filter-field--search">
          <label>Search</label>
          <input v-model="filters.search" type="text" class="dash-input" placeholder="Application no, applicant, certificate no…" @keyup.enter="applyFilters">
        </div>
      </div>

      <div class="filter-actions">
        <button class="btn-primary" :disabled="loading" @click="applyFilters">Apply filters</button>
        <button class="btn-outline" :disabled="loading" @click="resetFilters">Reset</button>
      </div>
    </div>

    <!-- ===== Error ===== -->
    <div v-if="error" class="alert-error">
      <feather-icon icon="AlertTriangleIcon" size="16" />
      <span>{{ error }}</span>
    </div>

    <!-- ===== Note from backend (data caveats) ===== -->
    <p v-if="summary.note" class="dash-note">{{ summary.note }}</p>

    <!-- ===== Loading ===== -->
    <div v-if="loading && !hasLoadedOnce" class="loading-wrap">
      <div class="spinner"></div>
      <span class="loading-text">Loading dashboard…</span>
    </div>

    <template v-else>
      <!-- ===== KPI ROW ===== -->
      <div class="kpi-grid">
        <div class="card kpi-card">
          <div class="kpi-icon" style="background: rgba(115, 103, 240, 0.12); color: #7367f0;">
            <feather-icon icon="FileTextIcon" size="16" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ formatNumber(kpis.totalApplications) }}</div>
            <div class="kpi-label">Total applications</div>
          </div>
        </div>

        <div class="card kpi-card">
          <div class="kpi-icon" style="background: rgba(0, 207, 232, 0.12); color: #00cfe8;">
            <feather-icon icon="TrendingUpIcon" size="16" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ formatNumber(kpis.avgApplicationsPerMonth) }}</div>
            <div class="kpi-label">Avg applications / month</div>
          </div>
        </div>

        <div class="card kpi-card">
          <div class="kpi-icon" style="background: rgba(40, 199, 111, 0.12); color: #28c76f;">
            <feather-icon icon="LayersIcon" size="16" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ formatNumber(kpis.activeSchemes) }}</div>
            <div class="kpi-label">Active schemes</div>
          </div>
        </div>

        <div class="card kpi-card">
          <div class="kpi-icon" style="background: rgba(255, 159, 67, 0.12); color: #ff9f43;">
            <feather-icon icon="UsersIcon" size="16" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ formatNumber(kpis.activeApplicants) }}</div>
            <div class="kpi-label">Active applicants</div>
          </div>
        </div>

        <div class="card kpi-card">
          <div class="kpi-icon" style="background: rgba(234, 84, 85, 0.12); color: #ea5455;">
            <feather-icon icon="MapPinIcon" size="16" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ formatNumber(kpis.activeProcessingOffices) }}</div>
            <div class="kpi-label">Active processing offices</div>
          </div>
        </div>
      </div>

      <!-- ===== Charts ===== -->
      <div class="charts-grid">
        <div class="card chart-card">
          <SchemeBarChart :data="summary.byCertificateScheme || []" />
        </div>
        <div class="card chart-card">
          <MonthlyTrendChart :data="summary.monthlyTrend || []" />
        </div>
        <div class="card chart-card">
          <ProductCategoryChart :data="summary.byProductCategory || []" />
        </div>
        <div class="card chart-card">
          <ProcessingOfficeChart :data="summary.byProcessingOffice || []" />
        </div>
      </div>

      <!-- ===== Top applicants ===== -->
      <div class="card top-applicants">
        <h4>Top applicants</h4>
        <div v-if="(summary.topApplicants || []).length" class="top-applicants__list">
          <div v-for="row in summary.topApplicants" :key="row.name" class="top-applicants__row">
            <span class="top-applicants__name">{{ row.name }}</span>
            <span class="top-applicants__total">{{ formatNumber(row.total) }}</span>
          </div>
        </div>
        <p v-else class="no-data">No data for the current filters.</p>
      </div>

      <!-- ===== Detail table ===== -->
      <div class="card detail-table">
        <div class="detail-table__header">
          <h4>Applications ({{ formatNumber(listMeta.total) }})</h4>
        </div>
        <div class="table-scroll">
          <table class="aa-table">
            <thead>
              <tr>
                <th @click="sortBy('applicationNo')">Application No#</th>
                <th @click="sortBy('allocationDate')">Allocation Date</th>
                <th>Scheme Owner</th>
                <th @click="sortBy('certificateScheme')">Certificate Scheme</th>
                <th @click="sortBy('applicationType')">Application Type</th>
                <th @click="sortBy('productCategory')">Product Category</th>
                <th>First Reviewer</th>
                <th>Second Reviewer</th>
                <th>Stage</th>
                <th>Status</th>
                <th>Certificate No.</th>
                <th @click="sortBy('issuedOn')">Issued On</th>
                <th>Expired On</th>
                <th @click="sortBy('applicantName')">Applicant</th>
                <th>Processing Office</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in listRows" :key="row.id">
                <td>{{ row.applicationNo }}</td>
                <td>{{ formatDate(row.allocationDate) }}</td>
                <td>{{ row.schemeOwner }}</td>
                <td>{{ row.certificateScheme }}</td>
                <td>{{ row.applicationType }}</td>
                <td>{{ row.productCategory }}</td>
                <td>{{ row.firstReviewer }}</td>
                <td>{{ row.secondReviewer }}</td>
                <td>{{ row.applicationStage }}</td>
                <td>{{ row.applicationStatus }}</td>
                <td>{{ row.certificateNumber || '—' }}</td>
                <td>{{ row.issuedOn ? formatDate(row.issuedOn) : '—' }}</td>
                <td>{{ row.expiredOn ? formatDate(row.expiredOn) : '—' }}</td>
                <td>{{ row.applicantName || '—' }}</td>
                <td>{{ row.processingOffice || '—' }}</td>
              </tr>
              <tr v-if="!listRows.length">
                <td colspan="15" class="no-data">No applications match the current filters.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Pagination
          :page="listMeta.page || 1"
          :per-page="listMeta.perPage || 25"
          :total-rows="listMeta.total || 0"
          @get-all-data="onPageChange"
        />
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'
import Pagination from '@/components/general-table/specific-utils/Pagination.vue'
import SchemeBarChart from './components/SchemeBarChart.vue'
import MonthlyTrendChart from './components/MonthlyTrendChart.vue'
import ProductCategoryChart from './components/ProductCategoryChart.vue'
import ProcessingOfficeChart from './components/ProcessingOfficeChart.vue'

const EMPTY_KPIS = {
  totalApplications: 0,
  avgApplicationsPerMonth: 0,
  activeSchemes: 0,
  activeApplicants: 0,
  activeProcessingOffices: 0,
}

export default {
  name: 'ApplicationsAnalyticsDashboard',
  components: {
    Pagination,
    SchemeBarChart,
    MonthlyTrendChart,
    ProductCategoryChart,
    ProcessingOfficeChart,
  },
  data() {
    return {
      loading: false,
      hasLoadedOnce: false,
      error: null,
      filters: {
        from: '',
        to: '',
        schemeOwner: [],
        certificateScheme: [],
        applicationType: [],
        productCategory: [],
        processingOffice: [],
        search: '',
      },
      filterOptions: {
        schemeOwners: [],
        certificateSchemes: [],
        applicationTypes: [],
        productCategories: [],
        processingOffices: [],
      },
      summary: {},
      listRows: [],
      listMeta: {
        page: 1, perPage: 25, total: 0, lastPage: 1,
      },
      sortState: { sortBy: 'allocationDate', sortDir: 'desc' },
    }
  },
  computed: {
    kpis() {
      return this.summary.kpis || EMPTY_KPIS
    },
  },
  mounted() {
    this.fetchFilterOptions()
    this.applyFilters()
  },
  methods: {
    formatNumber(n) {
      const v = Number(n || 0)
      return v.toLocaleString('en-US')
    },
    formatDate(d) {
      if (!d) return '—'
      return String(d).slice(0, 10)
    },
    queryParams(extra = {}) {
      const f = this.filters
      const params = { ...extra }
      if (f.from) params.from = f.from
      if (f.to) params.to = f.to
      if (f.schemeOwner.length) params.schemeOwner = f.schemeOwner
      if (f.certificateScheme.length) params.certificateScheme = f.certificateScheme
      if (f.applicationType.length) params.applicationType = f.applicationType
      if (f.productCategory.length) params.productCategory = f.productCategory
      if (f.processingOffice.length) params.processingOffice = f.processingOffice
      if (f.search) params.search = f.search
      return params
    },
    async fetchFilterOptions() {
      try {
        const res = await axios.get('applications-analytics/filters')
        const body = decryptData(res.data.payload)
        this.filterOptions = (body || {}).data || this.filterOptions
      } catch (e) {
        // Non-fatal to the page (it still works with empty dropdowns, and
        // fetchSummary's error below is the one shown to the user) but a
        // failure here was previously invisible even in devtools, which hid
        // a real backend 500 during development. Always log it.
        // eslint-disable-next-line no-console
        console.error('Failed to load Applications Analytics filter options', e)
      }
    },
    async fetchSummary() {
      const res = await axios.get('applications-analytics/summary', { params: this.queryParams() })
      const body = decryptData(res.data.payload)
      this.summary = (body || {}).data || {}
    },
    async fetchList(page = 1) {
      const params = this.queryParams({
        page,
        perPage: this.listMeta.perPage || 25,
        sortBy: this.sortState.sortBy,
        sortDir: this.sortState.sortDir,
      })
      const res = await axios.get('applications-analytics/list', { params })
      const body = decryptData(res.data.payload)
      const data = (body || {}).data || { data: [], meta: {} }
      this.listRows = data.data || []
      this.listMeta = data.meta || this.listMeta
    },
    async applyFilters() {
      this.loading = true
      this.error = null
      try {
        await Promise.all([this.fetchSummary(), this.fetchList(1)])
        this.hasLoadedOnce = true
      } catch (e) {
        this.error = (e && e.response && e.response.data && e.response.data.message)
          || (e && e.message)
          || 'Failed to load the Applications Analytics dashboard'
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = {
        from: '',
        to: '',
        schemeOwner: [],
        certificateScheme: [],
        applicationType: [],
        productCategory: [],
        processingOffice: [],
        search: '',
      }
      this.applyFilters()
    },
    sortBy(field) {
      if (this.sortState.sortBy === field) {
        this.sortState.sortDir = this.sortState.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortState.sortBy = field
        this.sortState.sortDir = 'desc'
      }
      this.fetchList(1)
    },
    onPageChange(page) {
      this.fetchList(page)
    },
  },
}
</script>

<style lang="scss" scoped>
.aa-dashboard {
  padding: 0.25rem 0 2rem;
}

.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
}
.dash-title {
  margin: 0;
  font-weight: 700;
}
.dash-sub {
  margin: 0.15rem 0 0;
  color: #6e6b7b;
  font-size: 0.85rem;
}

.btn-refresh, .btn-primary, .btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border-radius: 0.5rem;
  padding: 0.5rem 0.9rem;
  font-size: 0.85rem;
  cursor: pointer;
  border: 1px solid #d8d6de;
  background: #fff;
}
.btn-primary {
  background: #7367f0;
  border-color: #7367f0;
  color: #fff;
}
.btn-outline {
  background: #fff;
  color: #6e6b7b;
}
.spinning {
  animation: aa-spin 1s linear infinite;
}
@keyframes aa-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.card {
  background: #fff;
  border-radius: 0.75rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
  padding: 1rem;
}

.filter-bar {
  margin-bottom: 1.25rem;
}
.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 160px;
}
.filter-field--search {
  flex: 1 1 220px;
}
.filter-field label {
  font-size: 0.75rem;
  color: #6e6b7b;
  font-weight: 600;
}
.filter-field__hint {
  font-weight: 400;
  color: #b9b9c3;
  text-transform: none;
}
.filter-field__range {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.dash-input {
  border: 1px solid #d8d6de;
  border-radius: 0.4rem;
  padding: 0.4rem 0.5rem;
  font-size: 0.85rem;
}
.dash-input--multi {
  min-width: 170px;
}
.dash-period__sep {
  color: #6e6b7b;
}
.filter-actions {
  margin-top: 0.9rem;
  display: flex;
  gap: 0.6rem;
}

.alert-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(234, 84, 85, 0.12);
  color: #ea5455;
  border-radius: 0.5rem;
  padding: 0.7rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
}

.dash-note {
  font-size: 0.78rem;
  color: #6e6b7b;
  background: rgba(115, 103, 240, 0.06);
  border-radius: 0.5rem;
  padding: 0.6rem 0.9rem;
  margin-bottom: 1rem;
}

.loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 0;
  color: #6e6b7b;
}
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e9ecef;
  border-top-color: #7367f0;
  border-radius: 50%;
  animation: aa-spin 0.8s linear infinite;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
}
.kpi-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.kpi-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi-value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #5e5873;
}
.kpi-label {
  font-size: 0.75rem;
  color: #6e6b7b;
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
}
.chart-card {
  min-height: 320px;
}

.top-applicants {
  margin-bottom: 1.25rem;
}
.top-applicants h4 {
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
}
.top-applicants__list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.top-applicants__row {
  display: flex;
  justify-content: space-between;
  padding: 0.4rem 0.1rem;
  border-bottom: 1px dashed #ebe9f1;
  font-size: 0.85rem;
}
.top-applicants__name {
  color: #5e5873;
}
.top-applicants__total {
  font-weight: 600;
  color: #7367f0;
}

.detail-table__header {
  margin-bottom: 0.75rem;
}
.detail-table__header h4 {
  margin: 0;
  font-size: 0.95rem;
}
.table-scroll {
  overflow-x: auto;
}
.aa-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}
.aa-table th {
  text-align: left;
  padding: 0.6rem 0.6rem;
  border-bottom: 2px solid #ebe9f1;
  color: #6e6b7b;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}
.aa-table td {
  padding: 0.5rem 0.6rem;
  border-bottom: 1px solid #f5f5f7;
  white-space: nowrap;
}
.no-data {
  text-align: center;
  color: #6e6b7b;
  padding: 1.5rem 0;
}
</style>
